import { createRouter, createWebHistory } from "vue-router";
import type { RouteLocationNormalized } from "vue-router";
import type { UserRole } from "@/services/store";
import UserAuth from "@/modules/authentication/UserAuth.vue";
import UnauthorizedPage from "@/modules/authentication/UnauthorizedPage.vue";
import { intercomUpdate } from "@/services/intercom";
import { makeWorkOrderSearchParamsFromRoute } from "@/modules/workorder-list/workOrderSearchAdapters";
import { makeDraftFacetFromRoute } from "@/modules/draft-workorder-beta/draftListAdapters";
import { redirectToSavedDraftList } from "@/modules/draft-workorder-beta/draftListPreferences";
import { parseDate } from "@/helpers/time";
import { applicationInsightsInstance } from "@/services/application-insights/application-insights";
import { getEnvironmentTitlePrefix } from "@/services/environment";
import { reportRoutes } from "./modules/report/views/routes";
import { useAuthenticatedUserStore } from "@/services/store";
import { useWorkOrderSearchStore } from "@/modules/workorder-list/workOrderSearchStore";
import { chartDefaultDates, makeProjectSearchParametersFromRoute } from "@/modules/projects/routes";
import { startOfMonth } from "date-fns";
import { routes as transportationDocumentRoutes } from "./modules/transportationDocuments/routes";
import { parseLocationQueryStringArray, parseLocationQueryValueInt } from "@/core/router/routeParser";
import { DraftSortDirection, DraftSortFields } from "@/core/api/data-contracts";

function makeTitle(pageName?: string) {
  const title = pageName ?? "";
  const prefix = getEnvironmentTitlePrefix();
  const postFix = "Hubb";
  return `${prefix} ${title} - ${postFix}`;
}

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // A page that keeps its state in the query (filters, "Visa fler") stays where the user is.
    if (to.meta.keepScrollOnQueryChange && to.path === from.path) {
      return false;
    }
    return { top: 0, left: 0 };
  },
  routes: [
    {
      path: "/",
      name: "home",
      meta: { title: "Hubb", requiresAuth: true },
      component: () => import("@/modules/home/views/HomePage.vue"),
      alias: ["/home"],
    },
    {
      path: "/signin",
      name: "signin",
      meta: { skipUserInit: true, title: "Logga in", hideNavigation: true, hideAppBar: true },
      component: UserAuth,
      props: {
        login: true,
      },
    },
    {
      path: "/signout",
      name: "signout",
      meta: { skipUserInit: true, title: "Logga ut", hideNavigation: true, hideAppBar: true },
      component: UserAuth,
      props: {
        login: false,
      },
    },
    {
      path: "/authentication/logged-out",
      name: "authentication-logged-out",
      meta: {
        title: "Du är nu utloggad",
        skipUserInit: true,
        requiresAuth: false,
        hideNavigation: true,
        hideAppBar: true,
      },
      component: () => import("@/modules/authentication/LoggedOutPage.vue"),
    },
    {
      path: "/unauthorized",
      name: "Unauthorized",
      meta: { skipUserInit: true, title: "Behörighet saknas", hideNavigation: true, hideAppBar: true },
      component: UnauthorizedPage,
    },
    {
      path: "/noaccess",
      name: "noaccess",
      meta: { title: "Ingen åtkomst", hideNavigation: true, hideAppBar: true },
      component: () => import("@/modules/authentication/NoAccess.vue"),
    },
    {
      // Legacy ärenden can no longer be created; old links and bookmarks land in Ärenden 2.0
      path: "/createDraft",
      redirect: (to) => ({ name: "create-draft-beta", query: to.query }),
    },
    {
      path: "/create-draft-beta",
      name: "create-draft-beta",
      props: (route) => ({
        zendeskReference: route.query.zendeskReference,
      }),
      meta: {
        requiresAuth: true,
        title: "Skapa ärende",
      },
      component: () => import("@/modules/draft-workorder-beta/views/CreateDraft.vue"),
    },
    {
      path: "/create",
      name: "create",
      props: (route) => ({
        cloneWorkOrderId: route.query.cloneWorkOrderId,
        projectNumber: route.query.projectNumber,
        zendeskReference: route.query.zendeskReference,
        customerNumber: route.query.customerNumber,
        draftId: route.query.draftId,
        companyId: route.query.companyId ? String(route.query.companyId) : undefined,
        // TODO AOL: To be removed once migrated
        isBeta: route.query.isBeta === "true",
      }),
      meta: {
        requiresAuth: true,
        title: "Skapa arbetsorder",
      },
      component: () => import("@/modules/workorder-create/views/CreateWorkOrder.vue"),
    },

    {
      path: "/customers/search",
      name: "CustomerCreditSafeSearchView",
      props: (route) => ({
        titleProp: route.query?.q,
        customerTypeProp: route.query?.customerType ? Number(route.query.customerType) : undefined,
      }),
      meta: {
        requiresAuth: true,
        title: "Skapa ny kund",
      },
      component: () => import("@/modules/customer/views/CustomerCreditSafeSearchView.vue"),
    },
    {
      path: "/customers",
      name: "customers",
      meta: {
        requiresAuth: true,
        title: "Kunder",
      },
      component: () => import("@/modules/customer/views/CustomersView.vue"),
      props: (route) => ({ searchTerm: route.query.q ?? undefined }),
    },
    {
      path: "/customers/:customerNumber",
      name: "customer",
      props: {
        default: true,
        appbar: (route) => ({
          id: route.params.customerNumber,
        }),
      },
      meta: {
        requiresAuth: true,
        title: "Kund",
      },
      components: {
        default: () => import("@/modules/customer/views/CustomerDetails.vue"),
        appbar: () => import("@/modules/customer/components/CustomerHeader.vue"),
      },
    },

    {
      path: "/work-orders",
      name: "work-orders",
      meta: {
        requiresAuth: true,
        title: "Arbetsordrar",
      },
      component: () => import("@/modules/workorder-list/views/WorkOrderSearch.vue"),
      props: (route) => {
        const searchOptions = makeWorkOrderSearchParamsFromRoute(route.query);
        const store = useWorkOrderSearchStore();
        store.saveSearchOptions(searchOptions);

        return {
          searchOptions: searchOptions,
        };
      },
    },
    {
      path: "/search",
      name: "work-orders-search",
      meta: {
        requiresAuth: true,
        title: "Sök arbetsordrar och ärenden",
      },
      component: () => import("@/modules/home/views/HomePageSearch.vue"),
      props: (route) => {
        return {
          searchTerm: route.query.searchTerm as string | undefined,
        };
      },
    },
    {
      path: "/work-orders/lookup",
      name: "work-orders-lookup",
      meta: { requiresAuth: true, title: "Söker arbetsorder..." },
      props: (route) => ({ workOrderNumber: route.query.workOrderNumber }),
      component: () => import("@/modules/workorder-details/views/WorkOrderLookup.vue"),
    },
    {
      path: "/risk-assessment/:workOrderId/create",
      name: "risk-assessment-create",
      meta: {
        requiresAuth: true,
        title: "Skapa riskbedömning",
      },
      props: (route) => ({
        workOrderId: route.params.workOrderId,
        riskAssessmentId: route.query.riskAssessmentId ?? null,
      }),
      component: () => import("@/modules/risks/views/CreateRiskAssessment.vue"),
    },
    {
      path: "/risk-templates/create",
      name: "risk-template-admin-create",
      meta: {
        requiresAuth: true,
        title: "Skapa riskmall",
        hideNavigation: true,
        hideAppBar: true,
      },
      component: () => import("@/modules/risks/views/AdminCreateRiskTemplate.vue"),
    },
    {
      path: "/risk-templates",
      name: "risk-template-admin-overview",
      meta: {
        requiresAuth: true,
        title: "Riskmallar",
      },
      component: () => import("@/modules/risks/views/AdminOverview.vue"),
    },
    {
      path: "/projects",
      name: "projects",
      meta: {
        requiresAuth: true,
        title: "Projekt",
      },
      component: () => import("@/modules/projects/views/ProjectSearch.vue"),
      props: (route) => {
        const searchOptions = makeProjectSearchParametersFromRoute(route.query);
        return {
          searchOptions: searchOptions,
        };
      },
    },
    {
      path: "/projects/:projectId",
      props: {
        default: true,
        appbar: (route) => ({
          id: route.params.projectId,
        }),
      },
      meta: {
        requiresAuth: true,
        title: "Projekt",
      },
      components: {
        default: () => import("@/modules/projects/views/ProjectDetails.vue"),
        appbar: () => import("@/modules/projects/components/ProjectHeader.vue"),
      },
      children: [
        {
          path: "",
          name: "project",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Projekt",
          },
          component: () => import("@/modules/projects/views/ProjectWorkOrderList.vue"),
        },
        {
          path: "chart",
          name: "project-chart",
          props: (route) => {
            const defaultDate = chartDefaultDates(new Date());
            return {
              projectId: route.params.projectId,
              ...defaultDate,
            };
          },
          meta: {
            requiresAuth: true,
            title: "Schema",
          },
          component: () => import("@/modules/projects/views/ProjectChart.vue"),
        },
      ],
    },
    {
      path: "/work-orders/:id",
      props: {
        default: true,
        appbar: (route) => ({
          id: route.params.id,
        }),
      },
      meta: {
        requiresAuth: true,
        title: "Arbetsorder",
      },
      components: {
        default: () => import("@/modules/workorder-details/views/WorkOrderDetails.vue"),
        appbar: () => import("@/modules/workorder-details/components/WorkOrderHeader.vue"),
      },
      children: [
        {
          path: "",
          name: "work-order",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Arbetsorder",
            view: "tabs",
          },
          component: () => import("@/modules/workorder-details/components/WorkOrderSummary.vue"),
        },
        {
          path: "time-reports",
          name: "work-order-time-reports",
          meta: {
            requiresAuth: true,
            title: "Tidrapporter",
            view: "tabs",
          },
          component: () => import("@/modules/timereport/components/WorkOrderTimeReportTab.vue"),
        },
        ...transportationDocumentRoutes,
        {
          path: "files",
          name: "work-order-files",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Filer",
            view: "tabs",
          },
          component: () => import("@/modules/files/components/WorkOrderFileTab.vue"),
        },
        {
          path: "risks",
          name: "work-order-risks",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Riskbedömning",
            view: "tabs",
          },
          component: () => import("@/modules/risks/views/WorkOrderRiskTab.vue"),
        },
        {
          path: "expense-report",
          name: "work-order-expense-report",
          meta: {
            requiresAuth: true,
            title: "Omkostnader",
            view: "tabs",
          },
          component: () => import("@/modules/expenses/components/WorkOrderCostsTab.vue"),
        },
        {
          path: "kai",
          name: "work-order-kai",
          props: true,
          meta: {
            requiresAuth: true,
            restrictToRoles: ["se.eaztimate.hubb.dev"],
            title: "kAI",
            view: "tabs",
          },
          component: () => import("@/modules/kai/components/WorkOrderKaiTab.vue"),
        },
        {
          path: "tools",
          name: "work-order-tools",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Verktyg",
            view: "tabs",
          },
          component: () => import("@/modules/tools/components/WorkOrderToolsTab.vue"),
        },
        {
          path: "qr-code",
          name: "work-order-qr-code",
          props: true,
          meta: {
            requiresAuth: true,
            title: "QR-kod",
            view: "tabs",
          },
          component: () => import("@/modules/workorder-details/views/WorkOrderQRCode.vue"),
        },
        {
          path: "pdf-preview",
          name: "work-order-pdf-preview",
          meta: {
            requiresAuth: true,
            title: "Översikt",
            hideNavigation: true,
            hideAppBar: true,
          },
          component: () => import("@/modules/workorder-details/views/WorkOrderPdfPreview.vue"),
        },
        {
          path: "self-inspections",
          name: "work-order-self-inspections",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Egenkontroller",
            view: "tabs",
          },
          component: () => import("@/modules/self-inspections/views/WorkOrderSelfInspectionsTab.vue"),
        },
        {
          path: "self-inspections/create",
          name: "work-order-self-inspections-create",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Egenkontroller",
            view: "tabs",
          },
          component: () => import("@/modules/self-inspections/views/SelfInspectionCreateView.vue"),
        },
        {
          path: "self-inspections/update",
          name: "work-order-self-inspections-update",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Ändra typ av egenkontroll",
            view: "tabs",
          },
          component: () => import("@/modules/self-inspections/views/SelfInspectionUpdateView.vue"),
        },
        {
          path: "self-inspections/pre",
          name: "work-order-self-inspections-pre-view",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Egenkontroller",
            view: "tabs",
          },
          component: () => import("@/modules/self-inspections/views/PreInspectionView.vue"),
        },
        {
          path: "self-inspections/pre/edit",
          name: "work-order-self-inspections-pre-edit",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Egenkontroller",
            view: "tabs",
          },
          component: () => import("@/modules/self-inspections/views/PreInspectionEdit.vue"),
        },
        {
          path: "self-inspections/post",
          name: "work-order-self-inspections-post-view",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Egenkontroller",
            view: "tabs",
          },
          component: () => import("@/modules/self-inspections/views/PostInspectionView.vue"),
        },
        {
          path: "self-inspections/post/edit",
          name: "work-order-self-inspections-post-edit",
          props: true,
          meta: {
            requiresAuth: true,
            title: "Egenkontroller",
            view: "tabs",
          },
          component: () => import("@/modules/self-inspections/views/PostInspectionEdit.vue"),
        },
        ...reportRoutes,
      ],
    },
    {
      path: "/work-orders/:workOrderId/transportation-docs/:id",
      name: "transportation-doc-fullpage",
      meta: {
        requiresAuth: true,
        title: "Transportdokument",
        hideNavigation: true,
        hideAppBar: true,
      },
      props: true,
      component: () => import("@/modules/transportationDocuments/views/TransportDocumentView.vue"),
    },
    { path: "/appointments", redirect: "/agenda" },
    {
      path: "/agenda",
      name: "agenda",
      props: (route) => ({
        date: route.query.date ?? new Date().toISOString().split("T")[0],
      }),
      meta: {
        requiresAuth: true,
        title: "Agenda",
      },
      component: () => import("@/modules/agenda/views/AgendaPage.vue"),
    },
    {
      path: "/staff-overview",
      name: "staff-overview",
      props: (route) => ({
        week: typeof route.query.week === "string" ? parseDate(route.query.week) : new Date(),
      }),
      meta: {
        title: "Tidrapporter",
        requiresAuth: true,
      },
      component: () => import("@/modules/staffoverview/views/StaffOverview.vue"),
    },
    {
      path: "/draft-overview/:draftId",
      name: "draft-overview",
      props: {
        default: (route: { params: { draftId: string } }) => ({
          draftId: route.params.draftId,
        }),
        appbar: (route: { params: { draftId: string } }) => ({
          draftId: route.params.draftId,
        }),
      },
      meta: {
        title: "Ärende",
        requiresAuth: true,
      },
      components: {
        default: () => import("@/modules/draft-workorder/views/DraftOverview.vue"),
        appbar: () => import("@/modules/draft-workorder/components/DraftOverviewHeader.vue"),
      },
    },
    {
      // Links already sent to Zendesk use the old beta path
      path: "/draft-overview-beta/:companyId/:draftId",
      redirect: (to) => ({ name: "draft-overview-v2", params: to.params }),
    },
    {
      path: "/draft-overview-v2/:companyId/:draftId",
      name: "draft-overview-v2",
      props: {
        default: (route: { params: { draftId: string; companyId: string } }) => ({
          draftId: route.params.draftId,
          companyId: route.params.companyId,
        }),
        appbar: (route: { params: { draftId: string; companyId: string } }) => ({
          draftId: route.params.draftId,
          companyId: route.params.companyId,
        }),
      },
      meta: {
        title: "Ärenden 2.0",
        requiresAuth: true,
      },
      components: {
        default: () => import("@/modules/draft-workorder-beta/views/DraftOverview.vue"),
        appbar: () => import("@/modules/draft-workorder-beta/components/DraftOverviewHeader.vue"),
      },
    },
    {
      path: "/drafts/:statusId?",
      name: "drafts",
      props: (route) => ({
        showOnlyOwn: (route.query.mine ?? "0") === "1",
        showNotAssigned: (route.query.notassigned ?? "0") === "1",
        state: route.query.state ? Number(route.query.state) : null,
        statusId: route.query.statusId ? Number(route.query.statusId) : null,
        includeCancelled: (route.query.includeCancelled ?? "0") === "1",
        cscAllRegion: (route.query.cscAllRegion ?? "0") === "1",
        officeId: parseLocationQueryStringArray(route.query.officeId),
        searchTerm: route.query.searchTerm ? String(route.query.searchTerm) : null,
        sortField: parseLocationQueryValueInt(route.query.sortField, 0), // Default: CreatedDate
        sortDirection: parseLocationQueryValueInt(route.query.sortDirection, 1), // Default: Descending
        offset: parseLocationQueryValueInt(route.query.offset, 0),
        limit: parseLocationQueryValueInt(route.query.limit, 50),
      }),
      meta: {
        title: "Ärenden",
        requiresAuth: true,
      },
      component: () => import("@/modules/draft-workorder/views/ListView.vue"),
    },
    {
      // Bookmarks and old links use the beta path
      path: "/drafts-beta",
      redirect: (to) => ({ name: "drafts-v2", query: to.query }),
    },
    {
      path: "/drafts-v2",
      name: "drafts-v2",
      props: (route) => ({
        showOnlyOwn: (route.query.mine ?? "1") === "1",
        showNotAssigned: (route.query.notassigned ?? "0") === "1",
        cscAllRegion: (route.query.cscAllRegion ?? "0") === "1",
        officeId: parseLocationQueryStringArray(route.query.officeId),
        searchTerm: route.query.searchTerm ? String(route.query.searchTerm) : null,
        facet: makeDraftFacetFromRoute(route.query),
        sortField: parseLocationQueryValueInt(route.query.sortField, DraftSortFields.CreatedDate),
        sortDirection: parseLocationQueryValueInt(route.query.sortDirection, DraftSortDirection.Descending),
        offset: parseLocationQueryValueInt(route.query.offset, 0),
        limit: parseLocationQueryValueInt(route.query.limit, 50),
      }),
      meta: {
        title: "Ärenden 2.0",
        requiresAuth: true,
        keepScrollOnQueryChange: true,
      },
      beforeEnter: redirectToSavedDraftList,
      component: () => import("@/modules/draft-workorder-beta/views/ListView.vue"),
    },
    {
      path: "/staff-overview/:userSignature",
      name: "staff-details",
      meta: {
        requiresAuth: true,
        title: "Personal",
      },
      props: (route) => ({
        userSignature: route.params.userSignature,
        week: typeof route.query.week === "string" ? parseDate(route.query.week) : new Date(),
      }),
      component: () => import("@/modules/staffoverview/views/StaffDetails.vue"),
    },
    {
      path: "/expense-overview",
      name: "expense-overview",
      meta: {
        requiresAuth: true,
        restrictToRoles: ["se.eaztimate.hubb.arbetsledare"],
        title: "Omkostnader",
      },
      props: (route) => ({
        date: typeof route.query.date === "string" ? parseDate(route.query.date) : new Date(),
        showMyWorkOrders: route.query.showAllWorkOrders !== "true",
      }),
      component: () => import("@/modules/expenseoverview/views/ExpenseOverview.vue"),
    },
    {
      path: "/remote-measurement/add-sensor",
      name: "add-sensor",
      meta: {
        title: "Lägg till sensor",
        requiresAuth: true,
      },
      props: (route) => ({
        workOrderId: route.query.workOrderId,
        returnUrl: route.query.returnUrl,
      }),
      component: () => import("@/modules/remote-measurement/views/AddSensorToWorkOrderPage.vue"),
    },
    {
      path: "/remote-measurement/:workOrderId/sensor/:sensorId",
      name: "work-order-sensor-details",
      meta: {
        title: "Fjärrmätare",
        requiresAuth: true,
      },
      props: (route) => ({
        sensorId: route.params.sensorId,
        workOrderId: route.params.workOrderId,
      }),
      component: () => import("@/modules/remote-measurement/views/SensorDetails.vue"),
    },
    {
      path: "/remote-measurement-corroventa/:workOrderId/project/:projectId",
      name: "work-order-corroventa-project-details",
      meta: {
        title: "Fjärrmätare",
        requiresAuth: true,
      },
      props: (route) => ({
        projectId: route.params.projectId,
        workOrderId: route.params.workOrderId,
      }),
      component: () => import("@/modules/remote-measurement/views/CorroventaProjectDetails.vue"),
    },
    {
      name: "zendesk",
      path: "/zendesk/:zendeskTicketId",
      props: (route) => ({
        zendeskTicketId: route.params.zendeskTicketId,
      }),
      component: () => import("@/modules/zendesk/views/ZendeskLandingPage.vue"),
      meta: {
        title: "Zendesk",
        requiresAuth: true,
      },
    },
    {
      name: "reporting-monthly-timereports",
      path: "/reporting/time-reports/monthly",
      component: () => import("@/modules/staffoverview/views/MonthlyOverview.vue"),
      props: (route) => ({
        month:
          route.query.month && typeof route.query.month === "string"
            ? startOfMonth(parseDate(route.query.month))
            : startOfMonth(new Date()),
      }),
      meta: {
        title: "Månadsöversikt",
        requiresAuth: true,
      },
    },
    {
      name: "reporting-time-report-recommendations",
      path: "/reporting/time-reports/recommendations/:date",
      component: () => import("@/modules/report/views/TimeReportRecommendation.vue"),
      props: (route) => ({
        date: route.params.date,
      }),
      meta: {
        title: "Rekomendationer för tidrapporter",
        requiresAuth: true,
      },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      meta: { title: "Sidan kan inte hittas", hideNavigation: true, hideAppBar: true },
      component: () => import("@/components/PageNotFound.vue"),
    },
  ],
});

router.beforeEach(async (to, _, next) => {
  const store = useAuthenticatedUserStore();

  function hasAccess(meta: RouteLocationNormalized["meta"]) {
    if (!store.user.roles.includes("se.eaztimate.hubb")) {
      return false;
    }

    const restrictToRoles = meta?.restrictToRoles;
    return !restrictToRoles || restrictToRoles.some((r: UserRole) => store.user.roles.includes(r));
  }

  const { requiresAuth, skipUserInit, title } = to.meta ?? {};
  document.title = makeTitle(title);

  if (requiresAuth || !skipUserInit) {
    await store.userInit();
  }

  if (requiresAuth) {
    if (store.user.isAuthenticated) {
      if (!hasAccess(to.meta)) {
        next({ name: "noaccess" });
        return;
      }
    } else {
      applicationInsightsInstance().trackTrace({
        message: "Authentication is required but the user is not. sending the user to the login",
        severityLevel: 2,
      });
      await store.userLogin(to.fullPath);
      next(false);
      return;
    }
  }

  if (to.meta?.accessLevel === "beta" && !store.isBetaGroup) {
    next({ name: "noaccess" });
    return;
  }

  next();
});

router.afterEach(() => {
  intercomUpdate();
});

export default router;
